"use client";

import React, { useState } from "react";
import Image from "next/image";

interface ConditionsSectionProps {
  heading?: string;
  description?: string;
  columns?: "1" | "2" | "3" | "4";
  items?: Array<{
    title?: string;
    image?: {
      secure_url?: string;
      public_id?: string;
      url?: string;
    };
    points?: Array<{
      label?: string;
      description?: string;
    }>;
  }>;
}

const COLOR_SCHEME = ["#00A76E", "#815AF6", "#ECC15B", "#B9433A"];

// Function to darken a color progressively from left to right
const getDarkerColor = (baseColor: string, index: number, total: number): string => {
  if (total <= 1) return baseColor;
  
  // Convert hex to RGB
  const hex = baseColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate darkness factor (0 = no change for first box, increases for right boxes)
  // Max 35% darker for the rightmost box
  const darknessFactor = (index / (total - 1)) * 0.35;

  // Darken the color by reducing RGB values
  const newR = Math.max(0, Math.floor(r * (1 - darknessFactor)));
  const newG = Math.max(0, Math.floor(g * (1 - darknessFactor)));
  const newB = Math.max(0, Math.floor(b * (1 - darknessFactor)));

  // Convert back to hex
  const toHex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

export default function ConditionsSection({
  heading,
  description,
  columns = "2",
  items,
}: ConditionsSectionProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  if (!items || items.length === 0) return null;

  const activeItem = activeIndex !== null ? items[activeIndex] : null;
  const activeColor = activeIndex !== null ? COLOR_SCHEME[activeIndex % 4] : null;

  const handleMouseEnter = (index: number) => {
    setHoveredIndex(index);
    setActiveIndex(index);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
    // Keep activeIndex unchanged so sub boxes remain visible
  };

  return (
    <section className="py-12">
      <div className="mx-auto max-w-6xl px-4">
        {heading && (
          <h2 className="mb-2 text-center text-3xl font-bold text-violet-950 md:text-4xl">
            {heading}
          </h2>
        )}
        {description && (
          <p className="mb-8 text-center text-lg text-gray-600">
            {description}
          </p>
        )}

        {/* Top three boxes - stay in place */}
        <div className="mb-8 grid grid-cols-2 justify-center gap-4 md:flex md:flex-wrap md:gap-6">
          {items.map((item, index) => {
            const isHovered = hoveredIndex === index;
            const baseColor = COLOR_SCHEME[index % 4];
            const boxColor = isHovered || activeIndex === index ? baseColor : "#F7F7F7";
            // Boxes 1, 2, 4 (indices 0, 1, 3) should have white text on dark background
            const shouldUseWhiteText = (isHovered || activeIndex === index) && (index % 4 === 0 || index % 4 === 1 || index % 4 === 3);

            return (
              <div
                key={index}
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
                className={`group relative h-[100px] w-full cursor-pointer rounded-3xl p-[6.5px] transition-all duration-300 md:h-[278px] md:w-[258px] ${
                  isHovered || activeIndex === index
                    ? "scale-105 shadow-lg"
                    : "hover:scale-105 hover:shadow-lg"
                }`}
                style={{
                  backgroundColor: boxColor,
                }}
              >
                {/* Inner box at top - Desktop only */}
                <div
                  className={`mb-[13px] hidden h-[119px] w-[245px] overflow-hidden rounded-2xl bg-[#E0E0E0] transition-colors duration-300 md:flex md:items-center md:justify-center ${
                    isHovered || activeIndex === index ? "bg-[#E8E8E8]" : "group-hover:bg-[#E8E8E8]"
                  }`}
                >
                  {item.image && (
                    <Image
                      src={item.image.secure_url || item.image.url || `https://res.cloudinary.com/dkaghqnvm/image/upload/${item.image.public_id}`}
                      alt={item.title || "Condition image"}
                      width={245}
                      height={119}
                      className="h-full w-full object-cover md:object-contain"
                      unoptimized
                    />
                  )}
                </div>

                {/* Image on mobile - Left bottom */}
                {item.image && (
                  <div className="absolute bottom-[8px] left-[13px] md:hidden">
                    <Image
                      src={item.image.secure_url || item.image.url || `https://res.cloudinary.com/dkaghqnvm/image/upload/${item.image.public_id}`}
                      alt={item.title || "Condition image"}
                      width={60}
                      height={60}
                      className="size-[60px] rounded-lg object-cover"
                      unoptimized
                    />
                  </div>
                )}

                {/* Title - Top left on mobile, bottom left on desktop */}
                {item.title && (
                  <h3 className={`absolute left-[13px] top-[13px] text-base font-semibold sm:text-lg md:bottom-[13px] md:top-auto md:text-[20px] ${
                    shouldUseWhiteText ? "text-white" : "text-violet-950"
                  }`}>
                    {item.title}
                  </h3>
                )}

                {/* Arrow - Bottom right on mobile and desktop */}
                <svg
                  className={`absolute bottom-[13px] right-[13px] size-5 transition-transform duration-300 ${
                    isHovered || activeIndex === index ? "translate-x-1" : "group-hover:translate-x-1"
                  } ${shouldUseWhiteText ? "text-white" : "text-gray-500"}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            );
          })}
        </div>

        {/* Points boxes below - only show when a box is active */}
        {activeItem &&
          activeItem.points &&
          activeItem.points.length > 0 &&
          activeColor && (
            <div className="animate-fade-in-up">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {activeItem.points.map((point, pointIndex) => {
                  // Boxes 1, 2, 4 (indices 0, 1, 3) should have white text on dark background
                  const shouldUseWhiteText = activeIndex !== null && (activeIndex % 4 === 0 || activeIndex % 4 === 1 || activeIndex % 4 === 3);
                  
                  return (
                  <div
                    key={pointIndex}
                    className="rounded-2xl border border-gray-200 p-6 shadow-sm transition-all duration-300 hover:shadow-md"
                    style={{
                      backgroundColor: activeColor,
                      animationDelay: `${pointIndex * 0.1}s`,
                    }}
                  >
                    {point.label && (
                      <div className={`mb-3 inline-flex items-center gap-2 rounded-full px-4 py-1 text-sm ${
                        shouldUseWhiteText 
                          ? "bg-white/20 text-white" 
                          : "bg-violet-950/10 text-violet-950"
                      }`}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                          className="size-4 shrink-0"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m4.5 12.75 6 6 9-13.5"
                          />
                        </svg>
                        {point.label}
                      </div>
                    )}
                    {point.description && (
                      <p className={`text-sm leading-relaxed md:text-base ${
                        shouldUseWhiteText ? "text-white" : "text-gray-600"
                      }`}>
                        {point.description}
                      </p>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          )}
      </div>
    </section>
  );
}
